/**
 * src/components/ui/Drawer.jsx — a side-sliding panel, for tasks that want
 * more room than Dialog's centred box but shouldn't leave the current page
 * context entirely (e.g. a client's full detail alongside a list).
 *
 * Same native `<dialog>` mechanics as Dialog.jsx (`showModal()`, `closedby`
 * + fallback, focus trap and restore for free) — only the CSS differs
 * (full-height, pinned to one edge, slides in). Kept as a separate
 * component rather than a `variant` prop on Dialog because the two have
 * different entrance directions and that's a real enough difference to
 * read clearly at the call site (`<Drawer>` vs `<Dialog>`), not just a
 * style tweak.
 */
import { useEffect, useId, useRef } from 'react';

/**
 * @param {{ title: string, onClose: () => void, side?: 'left'|'right', children: React.ReactNode }} props
 * @returns {JSX.Element}
 */
export function Drawer({ title, onClose, side = 'right', children }) {
  const ref = useRef(null);
  const titleId = useId();

  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;

    el.showModal();

    const handleClose = () => onClose();
    el.addEventListener('close', handleClose);

    let handleBackdropClick;
    if (!('closedBy' in HTMLDialogElement.prototype)) {
      handleBackdropClick = (e) => {
        if (e.target === el) el.close();
      };
      el.addEventListener('click', handleBackdropClick);
    }

    return () => {
      el.removeEventListener('close', handleClose);
      if (handleBackdropClick) el.removeEventListener('click', handleBackdropClick);
      // No explicit el.close() here — see Dialog.jsx's cleanup comment for
      // why: dialog.close()'s `close` event is asynchronous, so calling it
      // from a StrictMode dev-only synthetic cleanup permanently closed the
      // drawer for real once the next mount's listener picked up the stale
      // event. A detached <dialog> releases its own modal state.
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <dialog
      ref={ref}
      className={`ui-drawer ui-drawer--${side}`}
      closedby="any"
      aria-labelledby={titleId}
    >
      <div className="ui-dialog-header">
        <h2 id={titleId}>{title}</h2>
        <button type="button" className="btn btn-sm" onClick={() => ref.current?.close()}>
          Close
        </button>
      </div>
      {children}
    </dialog>
  );
}
